import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEventsByType, TimelineEvent } from '../../services/patient/timeline';
import TiptapContent, { TiptapDoc } from '../TiptapContent';

const STALE_MS = 60_000;

type ClarificationKind = 'request-revision' | 'clarification-requested' | 'clarification-provided';

interface ClarificationEntry {
    id: string;
    kind: ClarificationKind;
    text: string;
    /** Rich version of an ODO clinical question, when authored in the editor. */
    doc: TiptapDoc | null;
    createdAt: string;
}

interface KindMeta {
    label: string;
    badgeBg: string;
    badgeText: string;
}

const KIND_META: Record<ClarificationKind, KindMeta> = {
    'request-revision': {
        label: 'You asked for changes',
        badgeBg: 'bg-blue-100',
        badgeText: 'text-blue-800'
    },
    'clarification-requested': {
        label: 'ODO asked',
        badgeBg: 'bg-yellow-100',
        badgeText: 'text-yellow-800'
    },
    'clarification-provided': {
        label: 'You responded',
        badgeBg: 'bg-emerald-100',
        badgeText: 'text-emerald-800'
    }
};

function extractText(event: TimelineEvent, kind: ClarificationKind): string {
    const payload = event.payload as Record<string, unknown> | undefined;
    // 'clarification-provided' stores the GP answer under payload.response;
    // both 'request-revision' and 'clarification-requested' use payload.reason.
    const raw = kind === 'clarification-provided' ? payload?.response : payload?.reason;
    return typeof raw === 'string' ? raw : '';
}

// ODO clinical questions authored in the rich editor also carry `payload.reasonDoc`
// (the canonical Tiptap doc). When present, it renders formatted; otherwise the
// plain-text `reason` mirror is used.
function extractDoc(event: TimelineEvent, kind: ClarificationKind): TiptapDoc | null {
    if (kind !== 'clarification-requested') return null;
    const payload = event.payload as Record<string, unknown> | undefined;
    const doc = payload?.reasonDoc;
    return doc && typeof doc === 'object' && Array.isArray((doc as { content?: unknown }).content)
        ? (doc as TiptapDoc)
        : null;
}

function toEntry(kind: ClarificationKind) {
    return (event: TimelineEvent): ClarificationEntry => ({
        id: event.id,
        kind,
        text: extractText(event, kind),
        doc: extractDoc(event, kind),
        createdAt: event.createdAt
    });
}

function formatAbsolute(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

interface ClarificationHistoryProps {
    patientId: string;
    /** Optional header label. Defaults to "Previous clarifications". */
    title?: string;
    /** When true, render only the most recent ODO clinical question
     *  (clarification-requested) instead of the full back-and-forth. */
    latestQuestionOnly?: boolean;
    /** ISO cutoff: only entries at/after this instant are shown. Scopes the
     *  history to the current phase; omit to keep the full history. */
    since?: string | null;
    /** Timeline event type for "you asked for changes" entries: each phase's
     *  revision action logs under its own type. Default 'request-revision'. */
    revisionType?: string;
}

/**
 * Compact list of past clarification asks (ODO) and responses (doctor) for
 * a patient, sorted oldest-first. Returns `null` when there are no entries
 * so callers can drop it in without an empty-state branch.
 *
 * Used as contextual history in flows where the doctor is about to send a
 * new revision request — surfacing prior back-and-forth helps avoid
 * re-asking the same questions.
 */
export function ClarificationHistory({
    patientId,
    title = 'Previous clarifications',
    latestQuestionOnly = false,
    since = null,
    revisionType = 'request-revision'
}: ClarificationHistoryProps) {
    const scrollRef = useRef<HTMLDivElement>(null);

    const { data: entries, isError } = useQuery({
        queryKey: ['clarifications', patientId, since, revisionType],
        queryFn: async (): Promise<ClarificationEntry[]> => {
            const [revisions, asks, answers] = await Promise.all([
                fetchEventsByType(patientId, revisionType),
                fetchEventsByType(patientId, 'clarification-requested'),
                fetchEventsByType(patientId, 'clarification-provided')
            ]);
            const sinceMs = since ? new Date(since).getTime() : null;
            return [
                ...revisions.map(toEntry('request-revision')),
                ...asks.map(toEntry('clarification-requested')),
                ...answers.map(toEntry('clarification-provided'))
            ]
                .filter((e) => e.text.trim().length > 0)
                .filter((e) => sinceMs === null || new Date(e.createdAt).getTime() >= sinceMs)
                .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        },
        staleTime: STALE_MS,
        enabled: !!patientId
    });

    // Anchor the scroll on the most recent entry — the latest message is the
    // one the user most likely cares about (the ask being answered, or the
    // freshest context for a new revision request).
    useEffect(() => {
        // Only the full back-and-forth list anchors to the newest entry. The
        // single-question (respond) view renders inline and lets the modal
        // body scroll, so it must not bottom-anchor (which clipped the top).
        if (latestQuestionOnly) return;
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [entries, latestQuestionOnly]);

    // Surface load failure distinctly from "no clarifications yet". Without
    // this, a 5xx on the events fetch renders empty — visually identical to
    // a fresh case — and the doctor could re-ask a question they already
    // answered. A tiny fallback line keeps the contract that "no UI = no
    // history exists" honest.
    if (isError) {
        return (
            <div className="space-y-1.5">
                <h4 className="text-xs font-semibold text-gray-700">{title}</h4>
                <p className="text-xs text-red-600">
                    Couldn&apos;t load prior clarifications. Refresh to retry.
                </p>
            </div>
        );
    }

    // For the respond-to-question flow we only surface the latest ODO clinical
    // question; everywhere else shows the full back-and-forth.
    const displayed = latestQuestionOnly
        ? (entries ?? []).filter((e) => e.kind === 'clarification-requested').slice(-1)
        : (entries ?? []);

    if (displayed.length === 0) return null;

    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-2">
                <h4 className="text-xs font-semibold text-gray-700">{title}</h4>
                {displayed.length > 1 && (
                    <span className="text-[10px] text-gray-500">({displayed.length})</span>
                )}
            </div>
            <div
                ref={scrollRef}
                className={
                    latestQuestionOnly
                        ? 'space-y-2'
                        : 'space-y-2 max-h-[200px] overflow-y-auto scrollbar-hide pr-1'
                }
            >
                {displayed.map((entry, i) => {
                    const meta = KIND_META[entry.kind];
                    const isLatest = i === displayed.length - 1;
                    return (
                        <div
                            key={entry.id}
                            className={`rounded-md border overflow-hidden ${
                                isLatest
                                    ? 'border-yellow-200 bg-yellow-50/60'
                                    : 'border-gray-200 bg-gray-50/60'
                            }`}
                        >
                            <div className="px-3 py-1.5 flex items-center justify-between gap-2 border-b border-gray-100 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span
                                        className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.badgeBg} ${meta.badgeText} flex-shrink-0`}
                                    >
                                        {meta.label}
                                    </span>
                                    {isLatest && displayed.length > 1 && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex-shrink-0">
                                            · Latest
                                        </span>
                                    )}
                                </div>
                                <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                    {formatAbsolute(entry.createdAt)}
                                </span>
                            </div>
                            <div className="px-3 py-2">
                                {entry.doc ? (
                                    <TiptapContent doc={entry.doc} patientId={patientId} />
                                ) : (
                                    <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                                        {entry.text}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
